import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FaComments, FaWhatsapp, FaTimes, FaPhone } from 'react-icons/fa';
import { FaFacebookMessenger } from 'react-icons/fa6';
import { faFacebookMessenger } from '@fortawesome/free-brands-svg-icons';
import ProductCard from './ProductCard';
import OfferSection from './OfferSection';
import ChatScreen from './ChatScreen';
import styled from 'styled-components';


const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 250px;
  overflow: hidden;
  border-radius: 8px;
  margin-bottom: 10px;

  @media (max-width: 1067px) {
    height: 250px;
    margin-bottom: -60px;
    margin-top:-90px;
  }
`;

const CarouselContainer = styled.div`
  display: flex;
  height: 100%;
  transition: transform 0.5s ease-in-out;
`;

const CarouselImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 5px 10px;
    font-size: 0.9em;
    height: 100%;
    width:768px;
  }
`;

const FilterContent = ({
  isMobile,
  searchQuery,
  setSearchQuery,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
  priceRange,
  setPriceRange,
  selectedMainCategory,
  setSelectedMainCategory,
  selectedSubCategory,
  setSelectedSubCategory,
  apiMainCategories,
  apiSubCategories,
  categories,
  selectedCategories,
  handleCategoryChange,
  sortOption,
  setSortOption,
  handleApplyFilters,
  handleClearFilters,
  setIsMobileFilterOpen,
}) => (
  <div className={`bg-white ${isMobile ? '' : 'border border-gray-300 rounded-lg'} overflow-hidden`}>
    {/* Filter Header */}
    <div className={`bg-[#F0F2F2] px-4 py-3 ${isMobile ? '' : 'border-b border-gray-300'} flex items-center justify-between`}>
      <h2 className="text-lg font-bold text-gray-900">Filters</h2>
      {isMobile && (
        <button
          onClick={() => setIsMobileFilterOpen(false)}
          className="text-gray-600 hover:text-gray-900 p-1"
        >
          <FaTimes className="text-xl" />
        </button>
      )}
    </div>

    {/* Filter Content */}
    <div className={`p-4 space-y-6 ${isMobile ? 'max-h-[calc(100vh-180px)]' : 'max-h-[calc(100vh-120px)]'} overflow-y-auto custom-scrollbar`}>

      {/* Search Filter */}
      <div className="pb-4 border-b border-gray-300">
        <h3 className="text-base font-bold text-gray-900 mb-3">Search</h3>
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search products..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] focus:border-transparent text-sm"
        />
      </div>

      {/* Price Range Input */}
      <div className="pb-4 border-b border-gray-300">
        <h3 className="text-base font-bold text-gray-900 mb-3">Price Range</h3>
        <div className="flex gap-2 mb-3">
          <input
            type="number"
            value={minPrice}
            onChange={(e) => setMinPrice(Number(e.target.value))}
            placeholder="Min"
            className="w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] focus:border-transparent text-sm"
          />
          <input
            type="number"
            value={maxPrice}
            onChange={(e) => setMaxPrice(Number(e.target.value))}
            placeholder="Max"
            className="w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] focus:border-transparent text-sm"
          />
        </div>
        <input
          type="range"
          min="0"
          max="200000"
          value={priceRange[1]}
          onChange={(e) => {
            const value = parseInt(e.target.value);
            setPriceRange([0, value]);
            setMaxPrice(value);
          }}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, #FF9900 0%, #FF9900 ${(priceRange[1] / 200000) * 100}%, #E5E7EB ${(priceRange[1] / 200000) * 100}%, #E5E7EB 100%)`
          }}
        />
        <div className="mt-2 flex items-center justify-between text-xs text-gray-600">
          <span>৳{minPrice.toLocaleString()}</span>
          <span>৳{maxPrice.toLocaleString()}</span>
        </div>
      </div>

      {/* Main Category Filter */}
      <div className="pb-4 border-b border-gray-300">
        <h3 className="text-base font-bold text-gray-900 mb-3">Main Category</h3>
        <select
          value={selectedMainCategory}
          onChange={(e) => setSelectedMainCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] focus:border-transparent text-sm bg-white cursor-pointer"
        >
          <option value="">All main categories</option>
          {apiMainCategories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Sub Category Filter */}
      <div className="pb-4 border-b border-gray-300">
        <h3 className="text-base font-bold text-gray-900 mb-3">Sub Category</h3>
        <select
          value={selectedSubCategory}
          onChange={(e) => setSelectedSubCategory(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] focus:border-transparent text-sm bg-white cursor-pointer"
        >
          <option value="">All sub categories</option>
          {apiSubCategories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
      </div>

      {/* Category Checkboxes */}
      <div className="pb-4 border-b border-gray-300">
        <h3 className="text-base font-bold text-gray-900 mb-3">Filter by Tag</h3>
        <div className="space-y-2">
          {categories.map((category) => (
            <label
              key={category}
              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1.5 rounded transition-colors group"
            >
              <input
                type="checkbox"
                value={category}
                checked={selectedCategories.includes(category)}
                onChange={handleCategoryChange}
                className="w-4 h-4 text-[#FF9900] bg-white border-gray-400 rounded focus:ring-[#FF9900] focus:ring-2 cursor-pointer"
              />
              <span className="text-sm text-gray-800 group-hover:text-[#C45500] transition-colors">
                {category}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Sort Filter */}
      <div className="pt-2">
        <h3 className="text-base font-bold text-gray-900 mb-3">Sort</h3>
        <select
          value={sortOption}
          onChange={(e) => setSortOption(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF9900] focus:border-transparent text-sm bg-white cursor-pointer"
        >
          <option value="">Relevance</option>
          <option value="low_to_high">Price: Low → High</option>
          <option value="high_to_low">Price: High → Low</option>
          <option value="a_z">Name: A → Z</option>
          <option value="z_a">Name: Z → A</option>
          <option value="newest">Newest</option>
        </select>
      </div>
    </div>

    {/* Filter Actions */}
    <div className={`p-4 border-t border-gray-300 bg-white ${isMobile ? 'sticky bottom-0' : ''}`}>
      <button
        onClick={handleApplyFilters}
        className="w-full bg-[#232F3E] hover:bg-[#131921] text-white font-semibold py-3 rounded-lg transition-colors duration-200 mb-2"
      >
        Apply Filters
      </button>
      <button
        onClick={handleClearFilters}
        className="w-full bg-white hover:bg-gray-100 text-gray-900 font-semibold py-3 rounded-lg border border-gray-300 transition-colors duration-200"
      >
        Clear All
      </button>
    </div>
  </div>
);


const AllProduct = ({
  products,
  addToCart,
  toggleFavorite,
  isFavorite,
  cartItems,
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 200000]);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(200000);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('');
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [isChatPopupOpen, setIsChatPopupOpen] = useState(false);
  const [showButtonText, setShowButtonText] = useState(true);
  const [isOptionsMenuOpen, setIsOptionsMenuOpen] = useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [apiMainCategories, setApiMainCategories] = useState([]);
  const [apiSubCategories, setApiSubCategories] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [categoryProducts, setCategoryProducts] = useState(null);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [apiCategoriesWithId, setApiCategoriesWithId] = useState([]);


  const whatsAppPhoneNumber = '8801345688562';
  const whatsAppDefaultMessage = 'Hello! I visited your website and would like some information.';

  const [apiCategories, setApiCategories] = useState([]);
  const defaultCategories = ['Gadget', 'Toys', 'Home Accessories', 'PC Component', "Gym", "Smart Watch", "Light"];

  const BASE_URL = 'https://zeroplushub.sparkx-ai.shop';

  const handleMessengerClick = () => {
    window.open('https://m.me/elevateemart', '_blank');
    setIsOptionsMenuOpen(false);
  };

  const handleCallClick = () => {
    window.location.href = 'tel:+8809647001250';
    setIsOptionsMenuOpen(false);
  };

  const handleApplyFilters = () => {
    if (isMobileFilterOpen) {
      setIsMobileFilterOpen(false);
    }
  };



  // URL এ ?c=categoryId থাকলে auto-filter করো
  useEffect(() => {
    const categoryParam = searchParams.get('c');
    if (!categoryParam) return;
    if (apiCategoriesWithId.length === 0 && apiMainCategories.length === 0 && apiSubCategories.length === 0) return;

    const fetchOnLoad = async () => {
      // সব category তে খোঁজো (tag, main, sub)
      const matchInTag = apiCategoriesWithId.find(
        cat => cat.id === categoryParam || cat.name.toLowerCase() === categoryParam.toLowerCase()
      );
      const matchInMain = apiMainCategories.find(
        cat => cat.id === categoryParam || cat.name.toLowerCase() === categoryParam.toLowerCase()
      );
      const matchInSub = apiSubCategories.find(
        cat => cat.id === categoryParam || cat.name.toLowerCase() === categoryParam.toLowerCase()
      );

      const matched = matchInTag || matchInMain || matchInSub;
      const categoryId = matched?.id || categoryParam;

      // ── Dropdown auto-select ──
      if (matchInMain) {
        setSelectedMainCategory(matchInMain.id);
        setSelectedSubCategory('');
      } else if (matchInSub) {
        setSelectedSubCategory(matchInSub.id);
      } else if (matchInTag) {
        // Checkbox auto-select
        setSelectedCategories([matchInTag.name]);
      }

      await fetchProductsByCategory(categoryId);
    };

    fetchOnLoad();
  }, [searchParams, apiCategoriesWithId, apiMainCategories, apiSubCategories]);

  const handleClearFilters = () => {
    setSelectedCategories([]);
    setPriceRange([0, 200000]);
    setMinPrice(0);
    setMaxPrice(200000);
    setSearchQuery('');
    setSortOption('');
    setSelectedMainCategory('');
    setSelectedSubCategory('');
    setCategoryProducts(null);
  };

  // ── API থেকে category অনুযায়ী products load করার helper ──
  const fetchProductsByCategory = async (categoryId) => {
    setIsCategoryLoading(true);
    try {
      const url = `${BASE_URL}/api/products/category/${encodeURIComponent(categoryId)}?per_page=50`;
      const response = await fetch(url);
      const data = await response.json();
      setCategoryProducts(data.products?.length > 0 ? data.products : null);
    } catch (error) {
      console.error('Category fetch error:', error);
      setCategoryProducts(null);
    } finally {
      setIsCategoryLoading(false);
    }
  };

  // ── Main Category dropdown handler ──
  const handleMainCategoryChange = async (value) => {
    setSelectedMainCategory(value);
    setSelectedSubCategory('');
    setSelectedCategories([]);
    if (!value) {
      setCategoryProducts(null);
      return;
    }
    await fetchProductsByCategory(value);
  };

  // ── Sub Category dropdown handler ──
  const handleSubCategoryChange = async (value) => {
    setSelectedSubCategory(value);
    setSelectedCategories([]);
    if (!value) {
      // Sub category সরিয়ে দিলে main category এর products দেখাও
      if (selectedMainCategory) {
        await fetchProductsByCategory(selectedMainCategory);
      } else {
        setCategoryProducts(null);
      }
      return;
    }
    await fetchProductsByCategory(value);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/categories`);
        const data = await response.json();
        if (data.success && data.categories && data.categories.length > 0) {
          const activeCategories = data.categories.filter(cat => cat.isActive);

          // ── Tag categories (checkbox filter এর জন্য) ──
          const tagCategoriesWithId = activeCategories
            .filter(cat => cat.level === 'category')
            .map(cat => ({ id: cat.id, name: cat.name }));

          setApiCategoriesWithId(tagCategoriesWithId);
          setApiCategories(
            tagCategoriesWithId.length > 0
              ? tagCategoriesWithId.map(c => c.name)
              : []
          );

          // ── Main categories → object {id, name} ──
          const mainCats = activeCategories
            .filter(cat => cat.level === 'main')
            .map(cat => ({ id: cat.id, name: cat.name }));
          setApiMainCategories(mainCats);

          // ── Sub categories → object {id, name} ──
          const subCats = activeCategories
            .filter(cat => cat.level === 'subcategory')
            .map(cat => ({ id: cat.id, name: cat.name }));
          setApiSubCategories(subCats);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const categories = apiCategories.length > 0 ? apiCategories : defaultCategories;

  // ── Checkbox category handler (API call করে) ──
  const handleCategoryChange = async (event) => {
    const { value, checked } = event.target;

    let newSelectedCategories;
    if (checked) {
      newSelectedCategories = [...selectedCategories, value];
    } else {
      newSelectedCategories = selectedCategories.filter((cat) => cat !== value);
    }
    setSelectedCategories(newSelectedCategories);

    // Dropdown selections clear করো
    setSelectedMainCategory('');
    setSelectedSubCategory('');

    if (newSelectedCategories.length === 1) {
      const selectedCat = apiCategoriesWithId.find(cat => cat.name === newSelectedCategories[0]);
      const categoryId = selectedCat?.id || newSelectedCategories[0];
      await fetchProductsByCategory(categoryId);
    } else if (newSelectedCategories.length === 0) {
      setCategoryProducts(null);
    } else {
      // একাধিক checkbox select হলে API call করা সম্ভব নয়, local filter এ ফিরে যাও
      setCategoryProducts(null);
    }
  };

  useEffect(() => {
    const fetchCarouselImages = async () => {
      try {
        const response = await fetch(`${BASE_URL}/active_images`);
        const data = await response.json();
        if (data.status === 'success') {
          setCarouselImages(data.images);
        } else {
          setCarouselImages([
            'https://via.placeholder.com/800x300/33FF57/FFFFFF?text=Slide+1',
            'https://via.placeholder.com/800x300/3357FF/FFFFFF?text=Slide+2',
            'https://via.placeholder.com/800x300/FF33CC/FFFFFF?text=Slide+3',
          ]);
        }
      } catch (error) {
        setCarouselImages([
          'https://via.placeholder.com/800x300/33FF57/FFFFFF?text=Slide+1',
          'https://via.placeholder.com/800x300/3357FF/FFFFFF?text=Slide+2',
          'https://via.placeholder.com/800x300/FF33CC/FFFFFF?text=Slide+3',
        ]);
      }
    };

    fetchCarouselImages();
  }, []);

  const goToNextSlide = () => {
    setCurrentSlide((prevSlide) =>
      prevSlide === carouselImages.length - 1 ? 0 : prevSlide + 1
    );
  };

  useEffect(() => {
    if (carouselImages.length > 0) {
      const slideInterval = setInterval(goToNextSlide, 5000);
      return () => clearInterval(slideInterval);
    }
  }, [carouselImages.length]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowButtonText(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isMobileFilterOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileFilterOpen]);

  const filteredProducts = useMemo(() => {
    // categoryProducts থাকলে সেটা use করো, না হলে সব products
    const sourceProducts = categoryProducts ?? products;

    let filtered = sourceProducts.filter((product) => {
      const matchesSearch = searchQuery === '' ||
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()));

      // categoryProducts থাকলে category filter skip (API already filtered)
      const matchesCategory = categoryProducts !== null
        ? true
        : selectedCategories.length === 0 ||
        (product.category && selectedCategories.includes(product.category));

      // Dropdown category filter — API handle করছে, তাই এখানে true
      const matchesMainCategory = true;
      const matchesSubCategory = true;

      const matchesPrice =
        product.price >= minPrice &&
        product.price <= maxPrice;

      return matchesSearch && matchesCategory && matchesMainCategory && matchesSubCategory && matchesPrice;
    });

    if (sortOption) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortOption) {
          case 'low_to_high':
            return a.price - b.price;
          case 'high_to_low':
            return b.price - a.price;
          case 'a_z':
            return a.name.localeCompare(b.name);
          case 'z_a':
            return b.name.localeCompare(a.name);
          case 'newest':
            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [products, categoryProducts, selectedCategories, minPrice, maxPrice, searchQuery, sortOption]);

  const getWhatsAppLink = () => {
    return `https://wa.me/${whatsAppPhoneNumber}?text=${encodeURIComponent(whatsAppDefaultMessage)}`;
  };

  const toggleOptionsMenu = () => {
    setIsOptionsMenuOpen(prev => !prev);
    setIsChatPopupOpen(false);
  };

  const handleWhatsAppClick = () => {
    window.open(getWhatsAppLink(), '_blank');
    setIsOptionsMenuOpen(false);
  };

  const handleChatUIClick = () => {
    setIsChatPopupOpen(true);
    setIsOptionsMenuOpen(false);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const filterProps = {
    searchQuery, setSearchQuery,
    minPrice, setMinPrice,
    maxPrice, setMaxPrice,
    priceRange, setPriceRange,
    selectedMainCategory,
    setSelectedMainCategory: handleMainCategoryChange,   // ← API call করে
    selectedSubCategory,
    setSelectedSubCategory: handleSubCategoryChange,     // ← API call করে
    apiMainCategories,
    apiSubCategories,
    categories, selectedCategories,
    handleCategoryChange,
    sortOption, setSortOption,
    handleApplyFilters, handleClearFilters,
    setIsMobileFilterOpen,
  };

  return (
    <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]">

      {carouselImages.length > 0 && (
        <CarouselWrapper>
          <CarouselContainer style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
            {carouselImages.map((image, index) => (
              <CarouselImage key={index} src={image} alt={`Slide ${index + 1}`} />
            ))}
          </CarouselContainer>
        </CarouselWrapper>
      )}

      {/* Mobile Filter Button */}
      <div className="md:hidden px-4 mb-4 mt-4" style={{ position: 'relative', zIndex: 10 }}>
        <button
          onClick={() => setIsMobileFilterOpen(true)}
          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 flex items-center justify-center gap-2 text-gray-900 font-medium hover:bg-gray-50 transition-colors shadow-sm"
        >
          <FontAwesomeIcon icon={faFilter} className="text-[#FF9900]" />
          <span>Filters</span>
          {(selectedCategories.length > 0 || priceRange[1] !== 200000 || selectedMainCategory || selectedSubCategory) && (
            <span className="bg-[#FF9900] text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {selectedCategories.length + (priceRange[1] !== 200000 ? 1 : 0) + (selectedMainCategory ? 1 : 0) + (selectedSubCategory ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {/* Mobile Filter Sidebar (Slide-in) */}
      {isMobileFilterOpen && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[1001] md:hidden transition-opacity duration-300"
            onClick={() => setIsMobileFilterOpen(false)}
          />
          <div className={`fixed left-0 top-0 h-full w-[85%] max-w-[320px] bg-white z-[1002] md:hidden shadow-2xl transform transition-transform duration-300 ease-out ${isMobileFilterOpen ? 'translate-x-0' : '-translate-x-full'}`}>
            <FilterContent isMobile={true} {...filterProps} />
          </div>
        </>
      )}

      {/* Main Content Container */}
      <div className="flex flex-col md:flex-row gap-4 px-4 md:px-6">

        {/* Desktop Filter Sidebar */}
        <aside className="hidden md:block w-full md:w-[260px] flex-shrink-0 md:sticky md:top-4 h-fit">
          <FilterContent isMobile={false} {...filterProps} />
        </aside>

        {/* Products Section */}
        <main className="flex-1 min-w-0">
          {isCategoryLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="text-gray-500">Loading...</div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="bg-white border border-gray-300 rounded-lg p-8 md:p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="text-6xl mb-4">🔍</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Products Found</h3>
                <p className="text-gray-600 mb-4">
                  We couldn't find any products matching your filters.
                </p>
                <button
                  onClick={handleClearFilters}
                  className="inline-block bg-[#FFD814] hover:bg-[#F7CA00] text-gray-900 font-semibold px-6 py-2 rounded-lg transition-colors duration-200"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-4 px-2 flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">{filteredProducts.length}</span> results
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-5 gap-3 md:gap-4 pb-6">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="relative">
                    {product.PCW === 0 && (
                      <div className="absolute top-0 right-0 bg-[#00A650] text-white text-xs font-bold px-2 py-1 md:px-3 md:py-1.5 rounded-bl-lg z-10 shadow-md">
                        Free Delivery
                      </div>
                    )}
                    <ProductCard
                      product={product}
                      addToCart={addToCart}
                      toggleFavorite={toggleFavorite}
                      isFavorite={isFavorite}
                      cartItems={cartItems}
                      navigate={navigate}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && onPageChange && (
        <div className="flex justify-center items-center gap-2 mt-8 mb-12 px-4">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="flex items-center gap-2">
            {getPageNumbers().map((number, index) => (
              <React.Fragment key={index}>
                {number === '...' ? (
                  <span className="px-3 py-2 text-gray-500">...</span>
                ) : (
                  <button
                    onClick={() => onPageChange(number)}
                    className={`min-w-[40px] h-10 px-3 border rounded-lg font-medium transition-all duration-200 ${number === currentPage
                      ? 'bg-[#FF9900] text-white border-[#FF9900]'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-[#FF9900] hover:text-[#FF9900]'
                      }`}
                  >
                    {number}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-2"
          >
            <span className="hidden sm:inline">Next</span>
            <FontAwesomeIcon icon={faChevronRight} className="text-sm" />
          </button>
        </div>
      )}

      {/* Floating Chat/WhatsApp/Messenger/Call Buttons */}
      <div className="fixed bottom-20 md:bottom-6 right-6 z-[1000] flex flex-col items-end gap-3">
        <div className={`flex flex-col gap-3 mb-2 animate-fade-in ${isOptionsMenuOpen ? 'flex md:flex' : 'hidden md:flex'}`}>
          <button
            onClick={handleWhatsAppClick}
            className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-full w-14 h-14 shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
            title="WhatsApp"
          >
            <FaWhatsapp className="text-2xl" />
          </button>

          <button
            onClick={handleMessengerClick}
            className="flex items-center justify-center gap-2 bg-[#0084FF] hover:bg-[#0073E6] text-white rounded-full w-14 h-14 shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
            title="Messenger"
          >
            <FontAwesomeIcon icon={faFacebookMessenger} className="text-2xl" />
          </button>

          <button
            onClick={handleCallClick}
            className="flex items-center justify-center gap-2 bg-[#34B7F1] hover:bg-[#2AA3DD] text-white rounded-full w-14 h-14 shadow-lg transition-all duration-200 hover:scale-110 active:scale-95"
            title="Call Us"
          >
            <FaPhone className="text-xl" />
          </button>
        </div>

        {showButtonText && !isOptionsMenuOpen && (
          <div className={`bg-[#00A650] text-white px-4 py-2 rounded-full text-sm whitespace-nowrap shadow-lg transition-all duration-300 md:hidden ${showButtonText ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
            Chat with us!
          </div>
        )}

        <button
          onClick={toggleOptionsMenu}
          className={`md:hidden bg-[#00A650] hover:bg-[#008A3D] text-white rounded-full w-14 h-14 flex items-center justify-center text-2xl shadow-lg transition-all duration-200 hover:scale-110 active:scale-95 transition-transform ${isOptionsMenuOpen ? 'rotate-45' : ''}`}
        >
          {isOptionsMenuOpen ? <FaTimes /> : <FaComments />}
        </button>
      </div>

      {/* Chat Popup */}
      <div
        className={`fixed right-6 w-[90%] sm:w-[350px] h-[600px] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col z-[999] transition-all duration-400 ease-out ${isChatPopupOpen ? 'bottom-24' : '-bottom-[650px]'}`}
      >
        <ChatScreen onClose={() => setIsChatPopupOpen(false)} />
      </div>

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #888; border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #555; }
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 16px; height: 16px; border-radius: 50%;
          background: #FF9900; cursor: pointer;
          border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        input[type="range"]::-moz-range-thumb {
          width: 16px; height: 16px; border-radius: 50%;
          background: #FF9900; cursor: pointer;
          border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default AllProduct;